import { forwardRef, useContext, useImperativeHandle, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { classNames } from 'primereact/utils';
import { StyleClass } from 'primereact/styleclass';
import { Password } from 'primereact/password';
import { useDialogCrup, ToastService, Field } from '@iamsoftware/react-hooks';
import { Button } from 'primereact/button';

import { AppTopbarRef } from 'src/types/types';
import { LayoutContext } from 'src/layout/context/layoutcontext';

import { AuthService } from 'src/service/AuthService';
import { AppStorage } from '../service/UserStorage';

const AppTopbar = forwardRef<AppTopbarRef>((props, ref) => {

  const { layoutState, onMenuToggle, showProfileSidebar } = useContext(LayoutContext);

  const menubuttonRef = useRef(null);
  const topbarmenuRef = useRef(null);
  const topbarmenubuttonRef = useRef(null);
  const accountRef = useRef(null);
  const [passwordValue, setPasswordValue] = useState('');
  const [passwordVerifyValue, setPasswordVerifyValue] = useState('');
  const [submittingManually, setSubmittingManually] = useState(false);

  const rules = [
    { label: 'Tối thiểu 8 ký tự', check: (pw) => pw.length >= 8 },
    { label: 'Có ít nhất 1 chữ hoa', check: (pw) => /[A-Z]/.test(pw) },
    { label: 'Có ít nhất 1 chữ thường', check: (pw) => /[a-z]/.test(pw) },
    { label: 'Có ít nhất 1 chữ số', check: (pw) => /[0-9]/.test(pw) },
    { label: 'Có ít nhất 1 ký tự đặc biệt', check: (pw) => /[!@#$%^&*(),.?":{}|<>]/.test(pw) },
    { label: 'Không chứa khoảng trắng', check: (pw) => pw.length > 0 && !/\s/.test(pw) }
  ];

  const matchCheck = passwordValue === passwordVerifyValue && passwordValue.length > 0;
  const isRulesValid = rules.every(r => r.check(passwordValue));
  const allValid = isRulesValid && matchCheck;

  const handleOpenChangePassword = () => {
    setPasswordValue('');
    setPasswordVerifyValue('');
    changePassword();
  };

  useImperativeHandle(ref, () => ({
    menubutton: menubuttonRef.current,
    topbarmenu: topbarmenuRef.current,
    topbarmenubutton: topbarmenubuttonRef.current
  }));

  const logout = () => {
    AuthService.logout().then(() => {
      window.location.hash = '/login';
    });
  }


  const createDialogProps = {
    header: '!Đổi mật khẩu',
    width: '40rem',
    fields: [
      { field: 'oldPassword', header: 'Mật khẩu cũ', required: true, type: 'Password', className: 'md:col-12' },
      {
        field: 'newPassword', header: 'Mật khẩu mới', required: true, type: 'InputCustom', className: 'md:col-12',
        inputTemplate: ({ fieldProps }) => (
          <>
            <Password
              {...fieldProps}
              onChange={(e) => {
                fieldProps.onChange({ value: e.target.value });
                setPasswordValue(e.target.value);
              }}
              toggleMask
              feedback={false}
              inputClassName="w-full"
              className="w-full"
            />
            <div className="grid mt-2">
              {rules.map((r, i) => {
                const isValid = r.check(passwordValue);
                return (
                  <div key={i} className={`col-12 md:col-6 flex align-items-center mb-1 text-sm ${isValid ? 'text-green-500' : 'text-red-500'}`}>
                    <i className={`pi ${isValid ? 'pi-check-circle' : 'pi-times-circle'} mr-2`} />
                    <span>{r.label}</span>
                  </div>
                );
              })}
            </div>
          </>
        )
      },
      {
        field: 'newPasswordVerify', header: 'Nhập lại mật khẩu mới', required: true, type: 'InputCustom', className: 'md:col-12',
        inputTemplate: ({ fieldProps }) => (
          <>
            <Password
              {...fieldProps}
              onChange={(e) => {
                fieldProps.onChange({ value: e.target.value });
                setPasswordVerifyValue(e.target.value);
              }}
              toggleMask
              feedback={false}
              inputClassName="w-full"
              className="w-full"
            />
            <div className={`flex align-items-center mt-2 text-sm ${matchCheck ? 'text-green-500' : 'text-red-500'}`}>
              <i className={`pi ${matchCheck ? 'pi-check-circle' : 'pi-times-circle'} mr-2`} />
              <span>Mật khẩu nhập lại trùng khớp</span>
            </div>
          </>
        )
      }
    ] as Field[],
    createItem: item => {
      const { newPassword, newPasswordVerify } = item;
      if (newPassword.trim().length === 0) {
        return Promise.resolve({ isSuccess: 'N', messages: 'Mật khẩu mới không được để trống' });
      }
      if (newPassword.length < 8) {
        return Promise.resolve({ isSuccess: 'N', messages: 'Mật khẩu mới phải có ít nhất 8 ký tự' });
      }
      if (!/[a-z]/.test(newPassword)) {
        return Promise.resolve({ isSuccess: 'N', messages: 'Mật khẩu mới phải chứa ít nhất 1 chữ cái thường' });
      }
      if (!/[A-Z]/.test(newPassword)) {
        return Promise.resolve({ isSuccess: 'N', messages: 'Mật khẩu mới phải chứa ít nhất 1 chữ cái hoa' });
      }
      if (!/[0-9]/.test(newPassword)) {
        return Promise.resolve({ isSuccess: 'N', messages: 'Mật khẩu mới phải chứa ít nhất 1 chữ số' });
      }
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) {
        return Promise.resolve({ isSuccess: 'N', messages: 'Mật khẩu mới phải chứa ít nhất 1 ký tự đặc biệt' });
      }
      if (/\s/.test(newPassword)) {
        return Promise.resolve({ isSuccess: 'N', messages: 'Mật khẩu mới không được chứa khoảng trắng' });
      }
      if (newPassword !== newPasswordVerify) {
        return Promise.resolve({ isSuccess: 'N', messages: 'Mật khẩu mới và nhập lại mật khẩu không khớp' });
      }
      return AuthService.changePassword(item);
    },
    checkResponseError: data => {
      let error = '';
      if (data?.isSuccess === 'N') {
        error = data.messages || data.errors?.[0];
      }
      return error;
    },
    useSaveSplitButton: false,
    originalDialogProps: {
      onHide: () => close(),
      footer: (
        <div className="flex justify-content-end gap-2">
          <Button label="Thoát" icon="pi pi-times" outlined severity="secondary" size="small" onClick={() => close()} />
          {allValid && (
            <Button
              label="Lưu thay đổi"
              icon="pi pi-check"
              size="small"
              loading={submittingManually}
              onClick={async () => {
                const data = form.getValue();
                if (!data.oldPassword) {
                  ToastService.error('Vui lòng nhập mật khẩu cũ');
                  return;
                }
                setSubmittingManually(true);
                try {
                  const res = await AuthService.changePassword(data);
                  if (res.isSuccess === 'N') {
                    ToastService.error(res.messages || res.errors?.[0] || 'Có lỗi xảy ra');
                  } else {
                    ToastService.success('Đổi mật khẩu thành công');
                    close();
                  }
                } catch (e) {
                  ToastService.error('Có lỗi xảy ra khi đổi mật khẩu');
                } finally {
                  setSubmittingManually(false);
                }
              }}
            />
          )}
        </div>
      )
    }
  };

  const { render: renderDialogChangePassword, create: changePassword, close, form } = useDialogCrup(createDialogProps);

  return (
    <div className="layout-topbar">
      <Link to="/dashboard" className="layout-topbar-logo">
        <img src="/iam/iam-econtract.png" alt="IAM-ECONTRACT" style={{ height: '2.4rem' }} />
      </Link>

      <button ref={menubuttonRef} type="button" className="p-link layout-menu-button layout-topbar-button" onClick={onMenuToggle}>
        <i className="pi pi-bars" />
      </button>

      <button ref={topbarmenubuttonRef} type="button" className="p-link layout-topbar-menu-button layout-topbar-button" onClick={showProfileSidebar}>
        <i className="pi pi-ellipsis-v" />
      </button>

      <div ref={topbarmenuRef} className={classNames('layout-topbar-menu', { 'layout-topbar-menu-mobile-active': layoutState.profileSidebarVisible })}>
        <div className="house-holds">
        </div>

        <div className="topbar-profile">
          <StyleClass nodeRef={accountRef} selector="@next" enterClassName="hidden" enterActiveClassName="scalein" leaveToClassName="hidden" leaveActiveClassName="fadeout" hideOnOutsideClick>
            <button ref={accountRef} className="p-link layout-topbar-button w-auto" type="button" style={{ borderRadius: '10px', padding: '0 8px' }}>
              <i className="pi pi-user" />
              <p style={{ fontSize: '16px', marginTop: '10px', marginLeft: '5px' }}><b>{AppStorage.get('partyTaxId')}</b></p>
            </button>
          </StyleClass>
          <ul className="iam-topbar-menu list-none p-3 m-0 border-round shadow-2 hidden absolute surface-overlay origin-top w-full md:w-16rem sm:w-12rem mt-2 right-0 top-auto">
            <li>
              <Link to="/tai-khoan/thong-tin" className="p-ripple flex p-2 border-round align-items-center hover:surface-hover transition-colors transition-duration-150 cursor-pointer">
                <i className="pi pi-id-card mr-3"></i>
                <span>Thông tin tài khoản</span>
              </Link>
              <div className="p-ripple flex p-2 border-round align-items-center hover:surface-hover transition-colors transition-duration-150 cursor-pointer"
                onClick={handleOpenChangePassword}>
                <i className="pi pi-key mr-3"></i>
                <span>Đổi mật khẩu</span>
              </div>
              <hr style={{ margin: '0.25rem 0' }} />
              <div className="p-ripple flex p-2 border-round align-items-center hover:surface-hover transition-colors transition-duration-150 cursor-pointer"
                onClick={logout}>
                <i className="pi pi-power-off mr-3"></i>
                <span>Đăng xuất</span>
              </div>
            </li>
          </ul>
        </div>
      </div>
      {/*<div ref={topbarmenuRef} className={classNames('layout-topbar-menu', {'layout-topbar-menu-mobile-active': layoutState.profileSidebarVisible})}>*/}
      {/*  <div className="house-holds">*/}
      {/*  </div>*/}

      {/*  <button className="p-link layout-topbar-button" onClick={event => menu.current.toggle(event)}>*/}
      {/*    <i className="pi pi-user"/>*/}
      {/*    <span>Profile</span>*/}
      {/*  </button>*/}
      {/*  <p style={{fontSize: '16px', marginTop: '10px'}}><b>{AppStorage.get('partyTaxId')}</b></p>*/}

      {/*  <Menu model={accountMenus} popup ref={menu}/>*/}
      {/*</div>*/}

      {renderDialogChangePassword()}
    </div>
  );
});

AppTopbar.displayName = 'AppTopbar';

export default AppTopbar;